import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SocialAnalytics = ({ campaigns }) => {
  // Mock analytics data
  const engagementData = [
    { date: '01/01', impressions: 1200, clicks: 85, conversions: 12 },
    { date: '02/01', impressions: 1800, clicks: 120, conversions: 18 },
    { date: '03/01', impressions: 2200, clicks: 165, conversions: 24 },
    { date: '04/01', impressions: 1900, clicks: 142, conversions: 20 },
    { date: '05/01', impressions: 2500, clicks: 198, conversions: 28 },
    { date: '06/01', impressions: 2800, clicks: 215, conversions: 32 },
    { date: '07/01', impressions: 3100, clicks: 245, conversions: 38 }
  ];

  const platformPerformance = [
    { platform: 'Facebook', leads: 45, engagement: 8.5, roi: 320 },
    { platform: 'Instagram', leads: 38, engagement: 12.3, roi: 285 },
    { platform: 'WhatsApp', leads: 52, engagement: 15.8, roi: 410 },
    { platform: 'LinkedIn', leads: 28, engagement: 6.2, roi: 195 }
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Impresiones</p>
              <p className="text-2xl font-bold text-foreground">15.5K</p>
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Icon name="TrendingUp" size={14} className="mr-1" />
                +24% vs mes anterior
              </p>
            </div>
            <Icon name="Eye" size={32} className="text-blue-600" />
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Clics</p>
              <p className="text-2xl font-bold text-foreground">1.2K</p>
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Icon name="TrendingUp" size={14} className="mr-1" />
                +18% vs mes anterior
              </p>
            </div>
            <Icon name="MousePointer" size={32} className="text-green-600" />
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Conversiones</p>
              <p className="text-2xl font-bold text-foreground">172</p>
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Icon name="TrendingUp" size={14} className="mr-1" />
                +32% vs mes anterior
              </p>
            </div>
            <Icon name="Target" size={32} className="text-primary" />
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Costo por Lead</p>
              <p className="text-2xl font-bold text-foreground">$12.50</p>
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Icon name="TrendingDown" size={14} className="mr-1" />
                -8% vs mes anterior
              </p>
            </div>
            <Icon name="DollarSign" size={32} className="text-orange-600" />
          </div>
        </div>
      </div>

      {/* Engagement Trends */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="TrendingUp" size={24} className="mr-2 text-primary" />
          Tendencias de Engagement (Últimos 7 días)
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={engagementData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="impressions" stroke="#3b82f6" name="Impresiones" />
            <Line type="monotone" dataKey="clicks" stroke="#10b981" name="Clics" />
            <Line type="monotone" dataKey="conversions" stroke="#f59e0b" name="Conversiones" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Platform Performance */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={24} className="mr-2 text-primary" />
          Rendimiento por Plataforma
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={platformPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="platform" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="leads" fill="#3b82f6" name="Leads Generados" />
            <Bar dataKey="engagement" fill="#10b981" name="Engagement (%)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Platform Details Table */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Table" size={24} className="mr-2 text-primary" />
          Detalles por Plataforma
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plataforma</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Leads</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Engagement</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">ROI (%)</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Estado</th>
              </tr>
            </thead>
            <tbody>
              {platformPerformance?.map((platform) => (
                <tr key={platform?.platform} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">{platform?.platform}</td>
                  <td className="py-3 px-4 text-sm text-right text-foreground">{platform?.leads}</td>
                  <td className="py-3 px-4 text-sm text-right text-foreground">{platform?.engagement}%</td>
                  <td className="py-3 px-4 text-sm text-right text-green-600 font-medium">{platform?.roi}%</td>
                  <td className="py-3 px-4 text-right">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Activo
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SocialAnalytics;
